import { Loader } from "lucide-react";
import { FC, useState } from "react";

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useDeleteOrganizationMutation } from "@/data/mutations";
import { useDialogState } from "@/hooks/use-dialog-state";

type Props = {
  organizationName: string;
  workOSOrganizationId: string;
};

export const DeleteOrganizationDialog: FC<Props> = ({
  organizationName,
  workOSOrganizationId,
}) => {
  const { dialog, closeDialog } = useDialogState();
  const deleteOrganizationMutation = useDeleteOrganizationMutation();
  const [confirmName, setConfirmName] = useState("");

  const handleSubmit = () => {
    deleteOrganizationMutation.mutate(workOSOrganizationId);
  };

  return (
    <AlertDialog open={dialog?.name === "delete-organization"}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete {organizationName}</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete the{" "}
            <b className="text-black">{organizationName}</b> organization and
            all of its associated SKUs and tags? This action is irreversible.
          </AlertDialogDescription>
          <AlertDialogDescription>
            Please enter the organization name in the input field below to
            confirm.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div>
          <Input
            placeholder="Organization Name"
            onChange={(event) => {
              setConfirmName(event.target.value);
            }}
          />
        </div>
        <AlertDialogFooter>
          <Button
            variant="outline"
            onClick={closeDialog}
            disabled={deleteOrganizationMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleSubmit}
            disabled={
              deleteOrganizationMutation.isPending ||
              confirmName !== organizationName
            }
          >
            {deleteOrganizationMutation.isPending ? (
              <Loader className="animate-spin" />
            ) : null}
            Delete
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
